import React from 'react';
import { Upload, CheckSquare, Archive } from 'lucide-react';
import { SortLabel, type ViewMode as SortLabelViewMode } from '../SortLabel';
import { SelectionBar } from '../SelectionBar';
import { Breadcrumb, type BreadcrumbItem } from '../Breadcrumb';
import './DocsTopBar.css';

export type SortOption = 'created' | 'uploaded';

export interface DocsTopBarProps {
  /** Current sort option */
  sortBy?: SortOption;
  /** Sort change handler */
  onSortChange?: (sort: SortOption) => void;
  /** Upload button click handler */
  onUploadClick?: () => void;
  /** Upload button label */
  uploadLabel?: string;
  /** Label for 'created' sort option */
  sortCreatedLabel?: string;
  /** Label for 'uploaded' sort option */
  sortUploadedLabel?: string;

  // New docs view mode props
  /** Number of new documents since last viewed */
  newDocsCount?: number;
  /** Current view mode */
  viewMode?: SortLabelViewMode;
  /** View mode change handler */
  onViewModeChange?: (mode: SortLabelViewMode) => void;
  /** Label for new docs button */
  newDocsLabel?: string;
  /** Label for "N new received" indicator (use {{count}} for number) */
  newReceivedLabel?: string;
  /** FR-017 #7a — processed documents with no twin link (entry shown only when > 0) */
  unlinkedCount?: number;
  /** Label for the unlinked entry (use {{count}} for number) */
  unlinkedLabel?: string;
  /** Label for back to all button */
  backToAllLabel?: string;
  /** Prefix for dropdown options (e.g., "Ordered by") */
  orderedByPrefix?: string;
  /** Label for document date option in dropdown */
  documentDateOptionLabel?: string;
  /** Label for received date option in dropdown */
  receivedDateOptionLabel?: string;

  // Selection mode props
  /** Whether selection mode is active */
  selectionMode?: boolean;
  /** Handler to enter selection mode */
  onEnterSelectionMode?: () => void;
  /** Handler to exit selection mode */
  onExitSelectionMode?: () => void;
  /** Number of selected documents */
  selectedCount?: number;
  /** Total number of documents (for select all) */
  totalCount?: number;
  /** Handler for select all */
  onSelectAll?: () => void;
  /** Handler for deselect all */
  onDeselectAll?: () => void;
  /** Handler for download selected */
  onDownloadSelected?: () => void;
  /** Handler for delete selected */
  onDeleteSelected?: () => void;
  /** Label for "Select documents" menu item */
  selectDocumentsLabel?: string;
  /** Label for "Cancel selection" menu item */
  cancelSelectionLabel?: string;
  /** Label for "Download selected" menu item */
  downloadSelectedLabel?: string;
  /** Label for "Delete selected" menu item */
  deleteSelectedLabel?: string;
  /** Handler for restore selected */
  onRestoreSelected?: () => void;
  /** Label for "Restore selected" menu item */
  restoreSelectedLabel?: string;
  /** Handler for reprocess selected */
  onReprocessSelected?: () => void;
  /** Label for "Reprocess selected" */
  reprocessSelectedLabel?: string;
  /** Label for selected count in selection bar */
  selectedLabel?: string;
  /** Cancel button label */
  cancelLabel?: string;
  /** Select all label */
  selectAllLabel?: string;

  // Cross-page select-all props
  /** Whether to show the "select all matching" banner */
  showSelectAllBanner?: boolean;
  /** Handler when user clicks "select all matching" */
  onSelectAllMatching?: () => void;
  /** Whether all matching docs across all pages are selected */
  allMatchingSelected?: boolean;
  /** Total matching documents across all pages */
  totalMatching?: number;
  /** Whether loading all matching IDs */
  loadingAllIds?: boolean;
  /** Label for "Select all {count} matching documents" */
  selectAllMatchingLabel?: string;
  /** Label for "All {count} documents selected" */
  allSelectedConfirmLabel?: string;

  // Show deleted documents props
  /** Whether showing deleted documents is active */
  showDeleted?: boolean;
  /** Handler to toggle showing deleted documents */
  onShowDeletedToggle?: () => void;
  /** Label for "Show deleted documents" menu item */
  showDeletedLabel?: string;
  /** Label for "Hide deleted documents" menu item */
  hideDeletedLabel?: string;

  // Breadcrumb props
  /** Breadcrumb items to display */
  breadcrumbItems?: BreadcrumbItem[];
}

/**
 * DocsTopBar - Top bar for the Documents page.
 * Includes breadcrumb, sort controls, upload button, and selection mode.
 */
export const DocsTopBar: React.FC<DocsTopBarProps> = ({
  sortBy = 'created',
  onSortChange,
  onUploadClick,
  uploadLabel = 'Upload',
  sortCreatedLabel = 'Sort by document date',
  sortUploadedLabel = 'Sort by received date',
  // New docs view mode
  newDocsCount = 0,
  viewMode = 'all',
  onViewModeChange,
  newDocsLabel = 'New documents',
  newReceivedLabel = '{{count}} new received',
  unlinkedCount,
  unlinkedLabel,
  backToAllLabel = 'Back to all',
  orderedByPrefix = '',
  documentDateOptionLabel = 'Document date (issued)',
  receivedDateOptionLabel = 'Received date (uploaded)',
  // Selection mode
  selectionMode = false,
  onEnterSelectionMode,
  onExitSelectionMode,
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  onDeselectAll,
  onDownloadSelected,
  onDeleteSelected,
  selectDocumentsLabel = 'Select documents',
  cancelSelectionLabel: _cancelSelectionLabel = 'Cancel selection',
  downloadSelectedLabel = 'Download selected',
  deleteSelectedLabel = 'Delete selected',
  onRestoreSelected,
  restoreSelectedLabel = 'Restore selected',
  onReprocessSelected,
  reprocessSelectedLabel = 'Reprocess selected',
  selectedLabel = '{count} selected',
  cancelLabel = 'Cancel',
  selectAllLabel = 'Select all',
  // Cross-page select-all
  showSelectAllBanner = false,
  onSelectAllMatching,
  allMatchingSelected = false,
  totalMatching,
  loadingAllIds = false,
  selectAllMatchingLabel,
  allSelectedConfirmLabel,
  // Show deleted documents
  showDeleted = false,
  onShowDeletedToggle,
  showDeletedLabel = 'Show deleted documents',
  hideDeletedLabel: _hideDeletedLabel = 'Hide removed documents',
  // Breadcrumb
  breadcrumbItems,
}) => {
  return (
    <div className="docs-topbar">
      {/* Single row: Breadcrumb (left) + Kebab + Sort + Upload (right) */}
      <div className="docs-topbar__content">
        {/* Left: Breadcrumb navigation */}
        <div className="docs-topbar__left">
          {breadcrumbItems && breadcrumbItems.length > 0 && (
            <Breadcrumb items={breadcrumbItems} className="docs-topbar__breadcrumb" />
          )}
        </div>

        {/* Right: Sort label + Kebab menu + Upload button */}
        <div className="docs-topbar__right">
          <SortLabel
            sortBy={sortBy}
            onSortChange={(s) => onSortChange?.(s)}
            newDocsCount={newDocsCount}
            viewMode={viewMode}
            onViewModeChange={(m) => onViewModeChange?.(m)}
            createdLabel={sortCreatedLabel}
            uploadedLabel={sortUploadedLabel}
            newDocsLabel={newDocsLabel}
            newReceivedLabel={newReceivedLabel}
            unlinkedCount={unlinkedCount}
            unlinkedLabel={unlinkedLabel}
            backToAllLabel={backToAllLabel}
            orderedByPrefix={orderedByPrefix}
            documentDateOptionLabel={documentDateOptionLabel}
            receivedDateOptionLabel={receivedDateOptionLabel}
          />

          {/* Explicit mode buttons (replaced kebab menu) */}
          <button
            className={`docs-topbar__icon-button ${selectionMode ? 'docs-topbar__icon-button--active' : ''}`}
            onClick={selectionMode ? onExitSelectionMode : onEnterSelectionMode}
            aria-label={selectionMode ? _cancelSelectionLabel : selectDocumentsLabel}
            disabled={showDeleted}
          >
            <CheckSquare className="docs-topbar__icon-button-icon" />
          </button>
          <button
            className={`docs-topbar__icon-button ${showDeleted ? 'docs-topbar__icon-button--active' : ''}`}
            onClick={onShowDeletedToggle}
            aria-label={showDeleted ? _hideDeletedLabel : showDeletedLabel}
            disabled={selectionMode}
          >
            <Archive className="docs-topbar__icon-button-icon" />
          </button>

          <button className="docs-topbar__upload-button" onClick={onUploadClick}>
            <Upload className="docs-topbar__upload-icon" />
            <span>{uploadLabel}</span>
          </button>
        </div>
      </div>

      {/* Selection Bar - shown when in selection mode (but NOT in deleted mode, which has its own bar) */}
      {selectionMode && !showDeleted && (
        <SelectionBar
          selectedCount={selectedCount}
          totalCount={totalCount}
          onCancel={onExitSelectionMode || (() => {})}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          onDownload={onDownloadSelected}
          onReprocess={onReprocessSelected}
          onDelete={onDeleteSelected}
          onRestore={onRestoreSelected}
          showDeleted={showDeleted}
          selectedLabel={selectedLabel}
          cancelLabel={cancelLabel}
          selectAllLabel={selectAllLabel}
          downloadLabel={downloadSelectedLabel}
          reprocessLabel={reprocessSelectedLabel}
          deleteLabel={deleteSelectedLabel}
          restoreLabel={restoreSelectedLabel}
          showSelectAllBanner={showSelectAllBanner}
          onSelectAllMatching={onSelectAllMatching}
          allMatchingSelected={allMatchingSelected}
          totalMatching={totalMatching}
          loadingAllIds={loadingAllIds}
          selectAllMatchingLabel={selectAllMatchingLabel}
          allSelectedConfirmLabel={allSelectedConfirmLabel}
          className="docs-topbar__selection-bar"
        />
      )}

      {/* Deleted Mode Bar - uses SelectionBar with Restore button only */}
      {showDeleted && (
        <SelectionBar
          selectedCount={selectedCount}
          totalCount={totalCount}
          onCancel={onShowDeletedToggle || (() => {})}
          onSelectAll={onSelectAll}
          onDeselectAll={onDeselectAll}
          onRestore={onRestoreSelected}
          showDeleted={showDeleted}
          selectedLabel={selectedLabel}
          cancelLabel={cancelLabel}
          selectAllLabel={selectAllLabel}
          restoreLabel={restoreSelectedLabel}
          showSelectAllBanner={showSelectAllBanner}
          onSelectAllMatching={onSelectAllMatching}
          allMatchingSelected={allMatchingSelected}
          totalMatching={totalMatching}
          loadingAllIds={loadingAllIds}
          selectAllMatchingLabel={selectAllMatchingLabel}
          allSelectedConfirmLabel={allSelectedConfirmLabel}
          className="docs-topbar__selection-bar"
        />
      )}
    </div>
  );
};

export default DocsTopBar;
